import React, { useEffect, useState } from "react";
import './styles.css';
import UpcomingCards from "../../components/UpcomingCards";
import { Box, Tooltip, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SortBox from "../../components/SortBox";
import { colors } from "../../common/pallette/colors";
import { faDownLeftAndUpRightToCenter, faMaximize, faCalendarDays, faList } from "@fortawesome/free-solid-svg-icons";
import { sortData } from "../../common/SortSupport";
import Calendar from "../../components/Calendar";
import UpcomingCardsSkeleton from "../../components/UpcomingCardsSkeleton";
import { useDispatch, useSelector } from "react-redux";
import { setDisplayProjects, setProjects } from "../../redux/reducers/common";
import { formatDate } from "../../common/StaticExports";
import { getProjects } from "../../api/projects";

export const statusConstants = [
    {status: 'Not Started', color: colors.projectsStatus.notStarted},
    {status: 'Missed', color: colors.projectsStatus.missed},
    {status: 'At Risk', color: colors.projectsStatus.atRisk},
    {status: 'On Track', color: colors.projectsStatus.onTrack},
    {status: 'Completed', color: colors.projectsStatus.complete},
];

const commentGenerator = (status: number, due: string) => {
    if (status == 4) return `${statusConstants[status].status}`;
    return `${statusConstants[status].status}, due to complete on ${due}`;
};

/** Shared round icon toggle — same design as EvaluationUpcoming */
const IconToggleBtn = ({
    icon,
    isActive,
    tooltip,
    onClick,
    size = 26,
}: {
    icon: any;
    isActive: boolean;
    tooltip: string;
    onClick: () => void;
    size?: number;
}) => (
    <Tooltip title={tooltip} arrow placement="top">
        <Box
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: size,
                height: size,
                borderRadius: '50%',
                cursor: 'pointer',
                backgroundColor: isActive ? `${colors.blue.navy}18` : 'transparent',
                transition: 'background 0.15s',
                flexShrink: 0,
                '&:hover': { backgroundColor: `${colors.blue.navy}15` },
            }}
        >
            <FontAwesomeIcon
                icon={icon}
                style={{
                    fontSize: '12px',
                    color: isActive ? colors.blue.navy : colors.grey.dark,
                }}
            />
        </Box>
    </Tooltip>
);

const EvaluationProjects = (props: any) => {
    const { isExpanded, setIsExpanded, disableExpand } = props;

    const [showBy, setShowBy] = React.useState('list');
    const searchString = useSelector((state:any) => state.common.adminPageSearchString);
    const projectsData = useSelector((state:any) => state.common.projects);
    const displayProjectsData = useSelector((state:any) => state.common.displayProjects);
    const globalFilters = useSelector((state:any) => state.common.globalFilters);
    const clubNameFilter = useSelector((state:any) => state.common.clubNameFilter);
    const dispatch = useDispatch();
    const [isFetching, setIsFetching] = useState(false);

    const [order, setOrder] = React.useState('');
    const [orderBy, setOrderBy] = React.useState('');

    const optionData = [
        {property: 'ragb', name: 'Status desc'},
        {property: 'ragb', name: 'Status asc'},
        {property: 'name', name: 'Project name asc'},
        {property: 'name', name: 'Project name desc'},
        {property: 'dueDate', name: 'Date asc'},
        {property: 'dueDate', name: 'Date desc'},
    ];

    const handleRequestSort = (value: any) => {
        let property: any = optionData.find((d:any) => d.name == value);
        setOrder(value.includes('asc') ? 'asc' : 'desc');
        setOrderBy(property?.property);
    };

    const updatedData = React.useMemo(
        () => displayProjectsData?.length > 0 && sortData(displayProjectsData, orderBy, order),
        [order, orderBy, displayProjectsData],
    );

    useEffect(() => {
        let filteredProjects = projectsData;
        if (searchString !== '') {
            filteredProjects = projectsData.filter((d:any) =>
                d?.name?.toLowerCase().includes(searchString?.toLowerCase()) ||
                statusConstants[d?.ragb]?.status?.toLowerCase().includes(searchString?.toLowerCase()) ||
                (d?.dueDate !== null && d?.dueDate?.toString().includes(searchString))
            );
        }
        dispatch(setDisplayProjects(filteredProjects));
    }, [searchString, projectsData, clubNameFilter]);

    useEffect(() => {
        const fetchProjects = async () => {
            setIsFetching(true);
            const response = await getProjects({ archivedStatus: 0 });
            return response;
        };
        fetchProjects().then((d:any) => {
            dispatch(setProjects(d));
            setIsFetching(false);
        });
    }, []);


    const resultCount = displayProjectsData?.length ?? 0;
    const isSearchActive = searchString !== '';

    return (
        <React.Fragment>
            <Box className={'projectsWrapper'} sx={{ bgcolor: colors.grey.bright }}>

                {/* ── Header ── */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '4px' }}>

                    {/* Left: title + compact count badge */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0, overflow: 'hidden' }}>
                        <span className="collapseHeading" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>PROJECTS</span>
                        <Box
                            sx={{
                                fontSize: '10px',
                                fontWeight: 700,
                                px: '6px',
                                py: '1px',
                                borderRadius: '20px',
                                backgroundColor: isSearchActive
                                    ? (resultCount > 0 ? `${colors.blue.navy}15` : 'rgba(224,68,68,0.10)')
                                    : `${colors.grey.dark}18`,
                                color: isSearchActive
                                    ? (resultCount > 0 ? colors.blue.navy : '#D94040')
                                    : colors.grey.dark,
                                border: `1px solid ${isSearchActive
                                    ? (resultCount > 0 ? `${colors.blue.navy}30` : 'rgba(217,64,64,0.25)')
                                    : `${colors.grey.dark}30`}`,
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                            }}
                        >
                            {resultCount}
                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                {' '}{resultCount === 1 ? 'project' : 'projects'}
                            </Box>
                        </Box>
                    </Box>

                    {/* Right: sort + toggle icons — all circles, never squeeze */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '1px', flexShrink: 0, ml: '6px' }}>
                        {showBy === 'list' && (
                            <SortBox label={'Sort By'} options={optionData} handleClick={handleRequestSort}/>
                        )}

                        <IconToggleBtn
                            icon={faCalendarDays}
                            isActive={showBy === 'calendar'}
                            tooltip="Calendar View"
                            onClick={() => setShowBy('calendar')}
                        />
                        <IconToggleBtn
                            icon={faList}
                            isActive={showBy === 'list'}
                            tooltip="List View"
                            onClick={() => setShowBy('list')}
                        />
                        {/* Expand — always visible, right next to list icon */}
                        <Tooltip title={isExpanded ? 'Collapse' : 'Expand'} arrow placement="top">
                            <Box
                                onClick={setIsExpanded}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 26,
                                    height: 26,
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    transition: 'background 0.15s',
                                    '&:hover': { backgroundColor: `${colors.blue.navy}15` },
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={isExpanded ? faDownLeftAndUpRightToCenter : faMaximize}
                                    style={{ fontSize: '11px', color: colors.grey.dark }}
                                />
                            </Box>
                        </Tooltip>
                    </Box>
                </Box>

                {/* ── Body ── */}
                <Box sx={{ marginTop: 1, height: 'calc(100% - 42px)', overflowY: showBy === 'calendar' ? 'hidden' : 'auto' }}>
                    {showBy === 'list' ? (
                        <Box>
                            {!isFetching && updatedData && updatedData?.length ? (
                                updatedData.map((project:any, index:number) => (
                                    <UpcomingCards
                                        key={index}
                                        statusColor={statusConstants[project.ragb]?.color}
                                        cardHeading={project.name}
                                        cardComment={commentGenerator(project.ragb, formatDate(project.dueDate))}
                                        index={index}
                                    />
                                ))
                            ) : isFetching ? (
                                Array.from({length: 8}, (_, i) => <UpcomingCardsSkeleton key={i} />)
                            ) : (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '140px',
                                    gap: '10px',
                                }}>
                                    <Box sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: '50%',
                                        backgroundColor: isSearchActive ? 'rgba(224,68,68,0.08)' : `${colors.grey.dark}12`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                        <FontAwesomeIcon
                                            icon={faList}
                                            style={{ fontSize: '16px', color: isSearchActive ? '#D94040' : colors.grey.dark, opacity: 0.6 }}
                                        />
                                    </Box>
                                    <Typography sx={{ fontSize: '12px', color: colors.grey.dark, textAlign: 'center', opacity: 0.7, maxWidth: '160px', lineHeight: 1.5 }}>
                                        {isSearchActive
                                            ? <>No projects match<br/><strong>"{searchString}"</strong></>
                                            : 'No projects found'
                                        }
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Calendar
                            data={projectsData?.map((d: any) => ({
                                title: d.name,
                                allDay: true,
                                date: formatDate(d?.dueDate),
                            }))}
                            globalSearch={searchString}
                        />
                    )}
                </Box>
            </Box>
        </React.Fragment>
    );
};

export default EvaluationProjects;
